/**
 * Global navigation service to manage custom back button logic
 * Pages can register custom back handlers that will be used by swipe gestures
 */

class NavigationService {
  constructor() {
    this.customBackHandler = null;
    this.customForwardHandler = null;
    this.currentRoute = null;
    this.forwardHistory = [];
    this.currentForwardIndex = -1;
  }

  /**
   * Register a custom back handler for the current page
   * @param {Function} handler - Custom back navigation function
   * @param {String} route - Current route path
   */
  registerBackHandler(handler, route) {
    this.customBackHandler = handler;
    this.currentRoute = route;
  }

  /**
   * Register a custom forward handler for the current page
   * @param {Function} handler - Custom forward navigation function
   * @param {String} route - Current route path
   */
  registerForwardHandler(handler, route) {
    this.customForwardHandler = handler;
    this.currentRoute = route;
  }

  /**
   * Clear the custom back handler (call on page leave)
   */
  clearBackHandler() {
    this.customBackHandler = null;
    this.currentRoute = null;
  }

  /**
   * Clear the custom forward handler (call on page leave)
   */
  clearForwardHandler() {
    this.customForwardHandler = null;
    this.currentRoute = null;
  }

  /**
   * Clear all forward history (call on direct navigation)
   */
  clearForwardHistory() {
    this.forwardHistory = [];
    this.currentForwardIndex = -1;
  }

  /**
   * Add entry to forward history
   * @param {Object} entry - Navigation entry with path, query, params, navigationType
   */
  addToForwardHistory(entry) {
    // Remove any forward history if we're navigating to a new route
    if (this.currentForwardIndex < this.forwardHistory.length - 1) {
      this.forwardHistory.splice(this.currentForwardIndex + 1);
    }

    this.forwardHistory.push({
      ...entry,
      timestamp: Date.now(),
    });

    this.currentForwardIndex = this.forwardHistory.length - 1;

    // Limit history size to prevent memory issues
    if (this.forwardHistory.length > 50) {
      this.forwardHistory.shift();
      this.currentForwardIndex--;
    }
  }

  /**
   * Check if forward navigation is available
   * @returns {Boolean}
   */
  canNavigateForward() {
    return this.currentForwardIndex < this.forwardHistory.length - 1;
  }

  /**
   * Get navigation type based on route transition
   * @param {Object} to - Target route
   * @param {Object} from - Source route
   * @returns {String} - Navigation type
   */
  getNavigationType(to, from) {
    // Bottom nav routes
    const bottomNavRoutes = ['/home', '/library', '/allpage', '/mepage'];

    if (bottomNavRoutes.includes(to.path) && bottomNavRoutes.includes(from.path)) {
      return 'direct'; // Clear forward history
    }

    // Progressive navigation (drill-down)
    if (to.path.startsWith(from.path) || this.isChildRoute(to, from)) {
      return 'progressive'; // Preserve forward history
    }

    return 'direct'; // Default: clear forward history
  }

  /**
   * Check if route is a child of another route
   * @param {Object} to - Target route
   * @param {Object} from - Source route
   * @returns {Boolean}
   */
  isChildRoute(to, from) {
    // Simple heuristic: if target path is longer and starts with source path
    return to.path !== from.path && to.path.startsWith(from.path);
  }

  /**
   * Execute back navigation using custom handler if available
   * @param {Object} router - Vue Router instance
   */
  goBack(router) {
    if (this.customBackHandler) {
      // Call the custom back handler
      this.customBackHandler();
    } else {
      // Use standard browser back
      if (window.history.length > 1) {
        router.back();
      } else {
        // If we're already on the homepage, don't navigate anywhere
        if (router.currentRoute.value.path === '/home' || router.currentRoute.value.path === '/') {
          console.log('Already on homepage, no back navigation needed');
          return;
        }
        router.push('/home');
      }
    }
  }

  /**
   * Execute forward navigation using custom handler if available
   * @param {Object} router - Vue Router instance
   */
  goForward(router) {
    if (this.customForwardHandler) {
      // Call the custom forward handler
      this.customForwardHandler();
    } else if (this.canNavigateForward()) {
      // Use forward history
      this.currentForwardIndex++;
      const nextRoute = this.forwardHistory[this.currentForwardIndex];
      router.push(nextRoute);
    } else {
      // No forward navigation available
      console.log('No forward navigation available');

      // Provide haptic feedback
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(50);
      }
    }
  }

  /**
   * Check if current page has a custom back handler
   */
  hasCustomHandler() {
    return this.customBackHandler !== null;
  }
}

// Export singleton instance
export default new NavigationService();
